import React, { useState, useContext, useEffect } from 'react';
import { useFormikContext } from 'formik';
import queryString from 'query-string';

import { FormContext } from 'contexts/FormContext';
import { DroneContext } from 'contexts/DroneContext';

import { CasaModal, Loader } from 'components/common';
import {
  AuCallout,
  AuInputLabel
} from 'components/DTA';
import {
  StepContainer,
  Declaration,
  FormNavigation,
  ApplicationName
} from 'components/CASAForm';

import { DroneData, DroneRegistrationFormData } from 'types/forms/droneRegistrationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { determineFee, DroneTable } from 'forms/Drones/Common';
import { useHmacService } from 'services';

const DroneRenewalSummaryStep: React.FC = () => {
  const { openStepById, setFormAlert, addFormAlert, setOnChangeValidation, setOnBlurValidation } = useContext(FormContext);
  const { referenceData, contextAlert, setContextAlert, submissionInProgress, manageDronesUrl, droneTableSorted, setDroneTableSorted, rateCards } = useContext(DroneContext);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedRemovalDrone, setSelectedRemovalDrone] = useState<number | null>(null);

  const { values, setValues, validateForm, setErrors, submitForm } = useFormikContext<DroneRegistrationFormData>();

  const [formValidated, setFormValidated] = useState<boolean>(false);

  const hmacService = useHmacService();
  const [hmacChecked, setHmacChecked] = useState(false);

  // runs on the first render
  useEffect(() => {
    const processHmac = async (returnParameters: any) => {
      try {
        const response = await hmacService.validateHmac({ paymentParamters: returnParameters });
        openStepById('Final', { successful: response?.wasSuccessful && response?.hmacValid && response?.paymentSuccess });
      }
      catch (error) {
        openStepById('Final', { successful: false });
      }
    };
    const returnParameters = queryString.parse(window.location.search);
    if (returnParameters.summaryCode === '0') {
      processHmac(returnParameters);
    }
    else {
      setHmacChecked(true);

      // Show error message if we have loaded a draft due to an error coming back from Westpac
      if (contextAlert) {
        setFormAlert(contextAlert);
        setContextAlert(null);
      }

      validateForm().then((validationErrors: any) => {
        let showUpdatesRequiredAlert = false;
        values.drones.forEach((drone, index) => {
          if (!drone.modelUpdateRequired) {
            drone.modelUpdateRequired = validationErrors.drones && validationErrors.drones[index] && validationErrors.drones[index].modelCode ? true : false;
          }
          if (!drone.serialNumberUpdateRequired) {
            drone.serialNumberUpdateRequired = validationErrors.drones && validationErrors.drones[index] && validationErrors.drones[index].serialNumber ? true : false;
          }
          if (drone.modelUpdateRequired || drone.serialNumberUpdateRequired) {
            showUpdatesRequiredAlert = true;
            drone.fee = 0;
          }
          else {
            const currentModel = drone.modelCode ? referenceData?.value['DRONE_MODEL'].find(model => model.casa_code === drone.modelCode) : null;
            drone.fee = determineFee(rateCards, currentModel?.droneWeightClassCode || drone.weightClassCode, drone.registeredOverseas === 'yes', drone.intendedUse === 'commercial');
          }
        });
        setErrors({}); // TODO: investage using resetForm()
        if (showUpdatesRequiredAlert) {
          const alertMessage =
            <>
              CASA requires additional details on some of your drones before they can be renewed.
              <br />
              <br />
              Any drone marked with a <span style={{ marginLeft: '.2rem' }} className='badge badge-update'>Updates required</span> badge below will need to be reviewed before it is eligible for renewal. Further information on what details is required for your drone can be found by clicking on that associated drone.
              <br />
              <br />
              You can continue with your drone registration renewal, however any drone marked with a <span style={{ marginLeft: '.2rem' }} className='badge badge-update'>Updates required</span> badge will not be renewed.
            </>;
          const alert: IFormAlertType = {
            as: 'warning',
            title: 'Updates required',
            message: alertMessage
          };
          addFormAlert(alert);
        }
        // sort drones requiring updates to the top of the list
        if (!droneTableSorted) {
          values.drones.sort((a, b) => Number(b.modelUpdateRequired ? b.modelUpdateRequired : b.serialNumberUpdateRequired) - Number(a.modelUpdateRequired ? a.modelUpdateRequired : a.serialNumberUpdateRequired));
          setDroneTableSorted(true);
        }
        setFormValidated(true);
        window.scrollTo(0, 0);
      });
    }

  }, []);

  // Open add drone screen
  const openDroneDetails = (id?: number) => {
    setFormAlert(null);
    const droneIndex = id ? values.drones.findIndex(drone => drone.id === id) : values.drones.length;
    openStepById('DroneDetails', { droneIndex });
  };

  const removeDrone = (id: number | null) => {
    // Handling edge cases
    if (id == null) {
      return;
    }

    const deletedDrone = values.drones.find(drone => drone.id === id);
    const drones = values.drones.filter(drone => drone != deletedDrone);

    setValues({
      ...values,
      drones: [
        ...drones
      ]
    });

    const alert: IFormAlertType = {
      as: 'success',
      title: 'Drone removed from application'
    };

    setFormAlert(alert);
    setModalVisible(false);
    setSelectedRemovalDrone(null);
    window.scrollTo(0, 0);

    // if all drones have been removed from the form data then send the user back to the begin step
    if (drones.length === 0) {
      openStepById('BeginRegistration');
    }
  };

  // Used to build the name of the drone
  const retrieveDroneName = (id: number | null): string => {
    if (id == null) {
      return '';
    }
    const selectedDrone = values.drones.find(drone => drone.id === id);

    return `${selectedDrone?.manufacturer} ${selectedDrone?.model || ''}`;
  };

  const generateDroneTableData = (intendedUse: string): any => {
    const drones = values.drones;
    return drones.filter(drone => drone.intendedUse === intendedUse).map(drone => {
      return {
        ...drone,
        name: retrieveDroneName(drone.id)
      };
    });
  };

  const removeDroneToggle = (id: number) => {
    setModalVisible(true);
    setSelectedRemovalDrone(id);
  };

  if (!referenceData || !referenceData.value || !formValidated || !hmacChecked) {
    return <Loader message='Working. Please wait.....' />;
  }

  const validateAndSubmit = async () => {
    setFormValidated(false);

    // Validate the form
    const validationErrors = await validateForm();

    // if declaration error then do not proceed
    if (validationErrors.declaration) {
      const alert: IFormAlertType = {
        as: 'error',
        title: 'There was a problem with the form',
        message: 'Please check the form below and try again'
      };
      setFormAlert(alert);
      setOnChangeValidation(true);
      setOnBlurValidation(true);
      window.scrollTo(0, 0);
    }
    else {
      // remove drones which have validation errors then submit the form
      if (validationErrors.drones) {
        const validDrones: Array<DroneData> = [];
        for (let index = 0; index < values.drones.length; index++) {
          if (!validationErrors.drones[index]) {
            validDrones.push({
              id: values.drones[index].id,
              droneRegistrationId: values.drones[index].droneRegistrationId,
              newExpiryDate: values.drones[index].newExpiryDate,
              serviceCatalogueCode: values.drones[index].serviceCatalogueCode,
              name: values.drones[index].name,
              manufacturer: values.drones[index].manufacturer,
              manufacturerCode: values.drones[index].manufacturerCode,
              model: values.drones[index].model,
              modelCode: values.drones[index].modelCode,
              modelUpdateRequired: values.drones[index].modelUpdateRequired,
              serialNumber: values.drones[index].serialNumber,
              serialNumberUpdateRequired: values.drones[index].serialNumberUpdateRequired,
              noSerialNumber: values.drones[index].noSerialNumber,
              weightClass: values.drones[index].weightClass,
              weightClassCode: values.drones[index].weightClassCode,
              category: values.drones[index].category,
              categoryCode: values.drones[index].categoryCode,
              registeredOverseas: values.drones[index].registeredOverseas,
              fee: values.drones[index].fee,
              droneId: values.drones[index].droneId,
              validUntil: values.drones[index].validUntil,
              validUntilMessage: values.drones[index].validUntilMessage,
              validUntilMessageLevel: values.drones[index].validUntilMessageLevel,
              renewedValidUntil: values.drones[index].renewedValidUntil,
              intendedUse: values.drones[index].intendedUse,
              isDraft: values.drones[index].isDraft,
              formCode: values.drones[index].formCode,
              updatesSaved: values.drones[index].updatesSaved,
            });
          }
        }
        if (validDrones.length === values.drones.length) {
          const alert: IFormAlertType = {
            as: 'error',
            title: 'There was a problem with the form',
            message: 'No drones are ready for renewal'
          };
          setFormAlert(alert);
          setOnChangeValidation(true);
          setOnBlurValidation(true);
          window.scrollTo(0, 0);
          return;
        }
        values.drones = validDrones;
      }
      setOnChangeValidation(false);
      setOnBlurValidation(false);
      setFormAlert(null);
      await submitForm();
    }
    setFormValidated(true);
  };

  const processTotalFee = () => {
    if (recreationalDroneData.length === 0 && droneData.length === 0) {
      return 0;
    }
    const recreationalTotalFee = recreationalDroneData.reduce((accumulator, current) => accumulator + (current.fee || 0), 0);
    const commercialTotalFee = droneData.reduce((accumulator, current) => accumulator + (current.fee || 0), 0);
    const totalFee = recreationalTotalFee + commercialTotalFee;
    return totalFee;
  };

  // setup data required for display
  const recreationalDroneData = generateDroneTableData('recreational');
  const droneData = generateDroneTableData('commercial');
  const totalFee = processTotalFee();
  const totalDrones = values.drones.length;
  const totalDronesToBeRenewed = values.drones.filter(d => !d.modelUpdateRequired && !d.serialNumberUpdateRequired).length;

  return (
    <>
      <ApplicationName name='Renew drone registration' />
      <StepContainer
        customHandleBack={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          window.location.href = manageDronesUrl;
        }}
      >
        <CasaModal
          heading='Remove drone'
          visible={modalVisible}
          toggle={setModalVisible}
          action={() => removeDrone(selectedRemovalDrone)}
        >
          <p>Are you sure you want to remove your drone?</p>
          <br />
          <AuInputLabel text='Drone' htmlFor='droneName' />
          {retrieveDroneName(selectedRemovalDrone)}

          {selectedRemovalDrone != null && values.drones.find(drone => drone.id === selectedRemovalDrone)?.serialNumber &&
            <>
              <br /><br />
              <AuInputLabel text='Serial number / registration mark' htmlFor='droneName' />
              {values.drones.find(drone => drone.id === selectedRemovalDrone)?.serialNumber}
            </>}
          <br />
          <p>Your drone will not be renewed</p>
        </CasaModal>
        <AuCallout>
          <h4>Your drone registration will not be renewed until you have submitted this form and paid the registration fee.</h4>
        </AuCallout>

        {recreationalDroneData.length > 0 &&
          <DroneTable
            caption={<>Recreational drones and <abbr title="Remotely piloted aircraft">RPA</abbr></>}
            droneData={recreationalDroneData}
            openDroneEdit={(id: number): void => {
              openDroneDetails(id);
            }}
            removeDroneToggle={(id: number): void => {
              removeDroneToggle(id);
            }}
            type='renewal'
            pageSize={15}
          />}
        {droneData.length > 0 &&
          <DroneTable
            caption={<>Commercial drones and <abbr title="Remotely piloted aircraft">RPA</abbr></>}
            droneData={droneData}
            openDroneEdit={(id: number): void => {
              openDroneDetails(id);
            }}
            removeDroneToggle={(id: number): void => {
              removeDroneToggle(id);
            }}
            type='renewal'
            pageSize={15}
          />}

        <b>Total: ${totalFee?.toFixed(2)}</b>
        <br />
        You are currently renewing {totalDronesToBeRenewed} of {totalDrones} drones that require renewal.

        <Declaration label='I agree to and understand the above conditions'>
          <p>I hereby declare that the particulars set out in this application are true in every respect. I acknowledge that to knowingly make a false or misleading statement in this application is an offence against the Criminal Code Act 1995 (Cth).</p>
          <p>I am aware and accept the risk that information sent electronically may be intercepted and read during transmission, not delivered or modified.</p>
          <p>I have read <a href="https://www.casa.gov.au/node/37026" target="_blank" rel="noopener noreferrer">CASA&apos;s policy on the collection and release of information</a> and I authorise CASA to release information contained on this form in accordance with that policy.</p>
        </Declaration>

        <FormNavigation
          nextButtonText={totalFee > 0 ? 'Proceed to payment' : 'Submit'}
          hidePrevious
          textBlurb={totalFee > 0 ? 'You will be directed to Westpac for payment' : ''}
          submissionInProgress={submissionInProgress}
          showSaveAndExit={false}
          onNextAction={validateAndSubmit} />
      </StepContainer>
    </>
  );
};

export default DroneRenewalSummaryStep;
